// =============================================================================
// iTravel 2.0 — Auth API Service
// =============================================================================
// Typed API functions for all authentication endpoints.
// These functions use the configured ky instance and return typed responses.
//
// Endpoints:
//   POST /auth/register        → Register a new account
//   POST /auth/login           → Authenticate with email & password
//   POST /auth/refresh         → Refresh access token
//   POST /auth/logout          → Revoke refresh token(s)
//   POST /auth/forgot-password → Request password reset email
//   POST /auth/reset-password  → Reset password with token
//   POST /auth/change-password → Change password (authenticated)
//   GET  /auth/me              → Get current user profile
//   GET  /auth/guest-invite    → What a trip invitation link signs up as
// =============================================================================

import type {
  AuthResponse,
  AuthTokens,
  ForgotPasswordPayload,
  LoginPayload,
  RefreshTokenPayload,
  RegisterPayload,
  ResetPasswordPayload,
  User,
} from '@itravel/shared';

import { api } from '@/lib/api/instance';

// ---------------------------------------------------------------------------
// Response envelope types (match server response format)
// ---------------------------------------------------------------------------

interface ApiDataResponse<T> {
  data: T;
}

interface MessageResponse {
  message: string;
}

// ---------------------------------------------------------------------------
// Auth API Functions
// ---------------------------------------------------------------------------

/**
 * Register a new user account.
 *
 * @param payload - Email, password, displayName, and optional preferredLanguage
 * @returns The created user profile and auth tokens
 */
export async function register(payload: RegisterPayload): Promise<AuthResponse> {
  const response = await api
    .post('auth/register', { json: payload })
    .json<ApiDataResponse<AuthResponse>>();

  return response.data;
}

/** Who a trip invitation link signs up as, and to which trip. */
export interface GuestInvitePreview {
  email: string;
  displayName: string;
  tripName: string | null;
  inviterName: string | null;
}

/**
 * Reads a guest invitation link. Null when it is invalid, expired or already
 * used — the form then works as a plain sign-up.
 */
export async function getGuestInvite(token: string): Promise<GuestInvitePreview | null> {
  try {
    const response = await api
      .get('auth/guest-invite', { searchParams: { token } })
      .json<ApiDataResponse<GuestInvitePreview>>();
    return response.data;
  } catch (error) {
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      (error as { response?: Response }).response?.status === 404
    ) {
      return null;
    }
    throw error;
  }
}

/**
 * Authenticate with email and password.
 *
 * @param payload - Email and password
 * @returns User profile and auth tokens
 */
export async function login(payload: LoginPayload): Promise<AuthResponse> {
  const response = await api
    .post('auth/login', { json: payload })
    .json<ApiDataResponse<AuthResponse>>();

  return response.data;
}

/**
 * Refresh an expired access token using a valid refresh token.
 *
 * @param payload - The refresh token to exchange
 * @returns New access and refresh tokens
 */
export async function refreshTokens(payload: RefreshTokenPayload): Promise<AuthTokens> {
  const response = await api
    .post('auth/refresh', { json: payload })
    .json<ApiDataResponse<AuthTokens>>();

  return response.data;
}

/**
 * Log out by revoking the specified refresh token.
 * If no refresh token is provided, all tokens for the user are revoked.
 *
 * @param refreshToken - Optional specific refresh token to revoke
 * @returns Success message
 */
export async function logout(refreshToken?: string): Promise<string> {
  const body = refreshToken ? { refreshToken } : undefined;

  const response = await api
    .post('auth/logout', { json: body })
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

/**
 * Request a password reset email.
 * Always returns success to prevent email enumeration.
 *
 * @param payload - The email address to send the reset link to
 * @returns Success message
 */
export async function forgotPassword(payload: ForgotPasswordPayload): Promise<string> {
  const response = await api
    .post('auth/forgot-password', { json: payload })
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

/**
 * Reset password using a token received via email.
 *
 * @param payload - Reset token and new password
 * @returns Success message
 */
export async function resetPassword(payload: ResetPasswordPayload): Promise<string> {
  const response = await api
    .post('auth/reset-password', { json: payload })
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

/**
 * Change password for the currently authenticated user.
 *
 * @param currentPassword - The user's current password for verification
 * @param newPassword - The new password to set
 * @returns Success message
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<string> {
  const response = await api
    .post('auth/change-password', {
      json: { currentPassword, newPassword },
    })
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

/**
 * Get the profile of the currently authenticated user.
 *
 * @returns The authenticated user's profile
 */
export async function getMe(): Promise<User> {
  const response = await api.get('auth/me').json<ApiDataResponse<User>>();

  return response.data;
}

// ---------------------------------------------------------------------------
// Named export object for convenient access
// ---------------------------------------------------------------------------

export const authApi = {
  register,
  getGuestInvite,
  login,
  refreshTokens,
  logout,
  forgotPassword,
  resetPassword,
  changePassword,
  getMe,
} as const;

export default authApi;
